import React, { useCallback, useEffect, useState } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import GenericLoadingText from '@apigrowfinance/pbolt-generic-loading-text';
import { Restricted } from '@apigrowfinance/pbolt-permission-provider';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { StatementItemType } from '@clients/statementsClient';
import ChangeStatementsDueDateModal from '@components/accountStatements/ChangeStatementsDueDateModal';
import NoStatements from '@components/accountStatements/skeletons/NoStatements';
import StatementSkeleton from '@components/accountStatements/skeletons/StatementSkeleton';
import StatementItem from '@components/accountStatements/StatementItem';
import LoadingError from '@components/loadingError/LoadingError';
import { useAccount } from '@contexts/AccountProvider';
import useGetAccountDueDates from '@hooks/queries/useGetAccountDueDates';
import useGetAvailableDueDates from '@hooks/queries/useGetAvailableDueDates';
import useGetStatements from '@hooks/queries/useGetStatements';
import useIsChildFinishedLoading from '@hooks/useIsChildFinishedLoading';
import getEnumTranslation from '@utils/getEnumTranslation';
import ordinalDateFormatter from '@utils/ordinalDateFormatter';
import isScrollbarVisible from '@utils/ScrollbarVisible';

const AccountStatements = () => {
    const [showEditModal, setShowEditModal] = useState(false);
    const { accountId } = useParams();
    const { account } = useAccount();
    const { locale } = useIntl();
    const { childFinishedLoading, setChildFinishedLoading } =
        useIsChildFinishedLoading();

    const {
        data: statements,
        isError,
        isLoading,
        fetchNextPage,
        hasNextPage,
    } = useGetStatements({ accountId });

    const {
        data: programDueDates,
        isError: isErrorProgramDueDates,
        isLoading: isLoadingProgramDueDates,
    } = useGetAccountDueDates(
        account?.program_id,
        account?.program_due_date_id,
    );

    const {
        data: availableDueDates,
        isError: isErrorAvailableDueDates,
        isLoading: isLoadingAvailableDueDates,
    } = useGetAvailableDueDates(parseFloat(accountId));

    const finishedLoading = useCallback(() => {
        if (hasNextPage) {
            setChildFinishedLoading(true);
        }
    }, [hasNextPage, setChildFinishedLoading]);

    // If the transactions are finished loading then update the state
    const loadMoreStuff = useCallback(() => {
        setChildFinishedLoading(false);
        fetchNextPage();
    }, [fetchNextPage, setChildFinishedLoading]);

    /*
        If we are at the top of the page, there are more pages to load and all transactions have finished loading then
        we need to load more statements otherwise the infinite scroll container will not load more as we can't scroll.
     */
    useEffect(() => {
        const atTop = window.pageYOffset === 0;
        if (
            hasNextPage &&
            atTop &&
            !isLoading &&
            !isScrollbarVisible() &&
            childFinishedLoading
        ) {
            loadMoreStuff();
        }
    }, [
        fetchNextPage,
        hasNextPage,
        isLoading,
        statements,
        childFinishedLoading,
        loadMoreStuff,
    ]);

    if (isError || isErrorProgramDueDates || isErrorAvailableDueDates) {
        return (
            <Box sx={{ mt: 10 }}>
                <LoadingError />
            </Box>
        );
    }

    return (
        <Grid container>
            {isLoading ||
            isLoadingProgramDueDates ||
            isLoadingAvailableDueDates ? (
                <StatementSkeleton />
            ) : statements?.length ? (
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                        }}>
                        <Typography
                            variant="overline"
                            component="h3"
                            data-testid="due-dates"
                            sx={{ mr: 1.25 }}>
                            <FormattedMessage
                                id="AccountStatements.dueDates"
                                defaultMessage="Statements due "
                            />
                            {programDueDates?.pagination?.items?.map((date) => {
                                return (
                                    ' ' +
                                    ordinalDateFormatter(date?.day, locale)
                                );
                            })}
                        </Typography>
                        <Restricted to="edit.dueDate">
                            <ChipButton
                                onClick={() => setShowEditModal(true)}
                                label={getEnumTranslation('edit')}
                                variant="editMenu"
                                isLoading={isLoading}
                            />
                        </Restricted>
                    </Box>
                    <InfiniteScroll
                        dataLength={statements?.length}
                        next={loadMoreStuff}
                        hasMore={hasNextPage && childFinishedLoading}
                        scrollThreshold={1}
                        loader={
                            <GenericLoadingText
                                displayText={
                                    <FormattedMessage
                                        id="AccountStatements.Loading"
                                        defaultMessage="Loading..."
                                    />
                                }
                                showLoader
                            />
                        }>
                        {statements?.map((statement: StatementItemType) => {
                            return (
                                <StatementItem
                                    key={statement.statement_id}
                                    statement={statement}
                                    accountId={parseFloat(accountId)}
                                    fetchNextStatement={finishedLoading}
                                />
                            );
                        })}
                    </InfiniteScroll>
                </Grid>
            ) : (
                <NoStatements />
            )}
            <ChangeStatementsDueDateModal
                accountId={accountId}
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                previousDay={programDueDates?.pagination?.items[0]?.day}
                availableDates={availableDueDates?.available_due_dates}
            />
        </Grid>
    );
};

export default AccountStatements;
